import { ApiMethods } from "typegram"
import { TgMailing } from "../interfaces"
import { ParseModeHelperText } from "../components/mailing/csv/helperText/parseModeHelperText"

export const AvailableMethods: TgMailing.AvailableMethods[] = [
  "sendMessage",
  "sendChatAction",
  "sendPhoto",
  "sendAnimation",
  "sendVideo",
  "forwardMessages",
  "copyMessages",
]
const ANY_TEXT_REGEXP = /.+/
const URL_REGEXP = /^https?:\/\/.+$/
const INTEGER_REGEXP = /^-?\d+$/
const MESSAGE_IDS_REGEXP = /^\s*-?\d+(\s*[,;\s]\s*-?\d+)*\s*$/
/**
 * Универсальный формат идентификатора чата для Telegram API (одинаково для
 * Bot API и MTProto):
 *   - число (возможно отрицательное, например "-1001234567890");
 *   - либо @username публичного канала/чата с **обязательным** префиксом "@"
 *     (Telegram-правила: 5–32 символа, латиница/цифры/"_", первая — буква).
 * Голый "channel_name", ссылки t.me/..., https://... — невалидны.
 */
const CHAT_ID_LIKE_REGEXP = /^(?:-?\d+|@[A-Za-z][A-Za-z0-9_]{4,31})$/
export const CHAT_ID_FORMAT_ERROR =
  "Укажите числовой ID чата или username, начинающийся с @"
const TOKEN_REGEXP = /^\d+:.+$/
const BOOL_REGEXP = /true|false/i
const PARSE_MODE_REGEXP = /Markdown|MarkdownV2|HTML/i
const CHAT_ACTION_REGEXP =
  /typing|upload_photo|record_video|upload_video|record_voice|upload_voice|upload_document|choose_sticker|find_location|record_video_note|upload_video_note/i

const CHAT_ID_COLUMN_HEADER_REGEXP = /.*id|user|юзер|ид.*/i
const FROM_CHAT_ID_COLUMN_HEADER_REGEXP =
  /.*from|source|origin|channel|username|handle|откуда|источник|оригинал|канал.*/i
const MESSAGE_IDS_COLUMN_HEADER_REGEXP =
  /.*message[_\s-]?ids?|msg[_\s-]?ids?|сообщ|пост.*/i
const TOKEN_COLUMN_HEADER_REGEXP = /.*token|bot|бот|токен.*/i
const TEXT_COLUMN_HEADER_REGEXP =
  /.*text|message|caption|текст|сообщение|подпись.*/i
const PARSE_MODE_COLUMN_HEADER_REGEXP = /.*format|parse|формат.*/i
const PHOTO_COLUMN_HEADER_REGEXP = /.*photo|img|image|url|фото|картинка.*/i
const GIF_COLUMN_HEADER_REGEXP =
  /.*gif|animation|image|url|гиф|картинка|анимация.*/i
const VIDEO_COLUMN_HEADER_REGEXP = /.*video|mp4|avi|url|видео.*/i
const NOTIFICATION_COLUMN_HEADER_REGEXP = /.*notif|alert|уведомл.*/i
const SPOILER_COLUMN_HEADER_REGEXP = /.*spoiler|hidd?e|спойлер|скрыть.*/i
const URL_PREVIEW_COLUMN_HEADER_REGEXP = /.*preview|web|предпросмотр.*/i
const DROP_AUTHOR_COLUMN_HEADER_REGEXP =
  /.*drop[_\s-]?author|anon|скрыть[_\s-]?автор.*/i
const PROTECT_CONTENT_COLUMN_HEADER_REGEXP = /.*protect|защит.*/i

const BOOLEAN_PARSER: TgMailing.BodyFieldProperties<
  TgMailing.AvailableMethods,
  any
>["parseValue"] = (v) => typeof v === "string" && v.toLowerCase() === "true"

/** Парсер строки вида "1, 2; 3 4" → [1, 2, 3, 4]. Используется и в CSV, и в manual-форме. */
export const parseMessageIds = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value.map((v) => Number(v)).filter((v) => Number.isFinite(v))
  }
  if (typeof value !== "string") return []
  return value
    .split(/[,;\s]+/g)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n))
}

/**
 * Нормализатор chat_id / from_chat_id для Telegram API (Bot API и MTProto).
 * Возвращает:
 *   - number для целочисленных ID (включая отрицательные);
 *   - `@${string}` для username с обязательным префиксом "@";
 *   - undefined для невалидного/пустого ввода. Невалидные значения
 *     (например, "channel" без "@", "https://t.me/...", "1.5") дополнительно
 *     отлавливаются `validateChatIdLike` и блокируются RHF до сабмита.
 */
export const parseChatIdLike = (
  value: unknown,
): number | `@${string}` | undefined => {
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : undefined
  }
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  if (trimmed.startsWith("@")) {
    return CHAT_ID_LIKE_REGEXP.test(trimmed)
      ? (trimmed as `@${string}`)
      : undefined
  }
  if (!INTEGER_REGEXP.test(trimmed)) return undefined
  const n = Number(trimmed)
  return Number.isInteger(n) ? n : undefined
}

/**
 * Валидатор для RHF (`rules.validate`). Возвращает `true` для валидного
 * значения, иначе — текст ошибки `CHAT_ID_FORMAT_ERROR`.
 */
export const validateChatIdLike = (value: unknown): true | string =>
  parseChatIdLike(value) === undefined ? CHAT_ID_FORMAT_ERROR : true

export const BaseParameters: Record<
  "chat_id" | "token",
  TgMailing.BodyFieldProperties<
    TgMailing.AvailableMethods,
    keyof TgMailing.ApiMethodBody[TgMailing.AvailableMethods]
  >
> = {
  chat_id: {
    required: true,
    validation: CHAT_ID_LIKE_REGEXP,
    parseValue: parseChatIdLike,
    columnHeaderMatch: CHAT_ID_COLUMN_HEADER_REGEXP,
    name: "ID чата / пользователя",
    helperText:
      "Целое число (например, -100…) или username с префиксом @ (например, @channel)",
  },
  token: {
    required: true,
    validation: TOKEN_REGEXP,
    columnHeaderMatch: TOKEN_COLUMN_HEADER_REGEXP,
    name: "Токен бота",
  },
}

export const SendMessageParameters: TgMailing.BodyFieldsDefinitions<
  "sendMessage",
  keyof Omit<Parameters<ApiMethods<any>["sendMessage"]>[0], "chat_id">
>["sendMessage"] = {
  text: {
    required: true,
    validation: ANY_TEXT_REGEXP,
    columnHeaderMatch: TEXT_COLUMN_HEADER_REGEXP,
    name: "Текст рассылки",
  },
  message_thread_id: {
    required: false,
    validation: INTEGER_REGEXP,
    hidden: true,
  },
  parse_mode: {
    required: false,
    validation: PARSE_MODE_REGEXP,
    columnHeaderMatch: PARSE_MODE_COLUMN_HEADER_REGEXP,
    name: "Форматирование",
    helperText: ParseModeHelperText,
    hidden: false,
  },
  entities: {
    required: false,
    validation: undefined,
    hidden: true,
  },
  disable_web_page_preview: {
    name: "Отключить предпросмотр веб-страниц",
    required: false,
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    columnHeaderMatch: URL_PREVIEW_COLUMN_HEADER_REGEXP,
    hidden: false,
  },
  disable_notification: {
    name: "Отключить уведомление",
    required: false,
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    columnHeaderMatch: NOTIFICATION_COLUMN_HEADER_REGEXP,
    hidden: false,
  },
  protect_content: {
    name: "Защитить контент",
    required: false,
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    helperText: "Запретить пересылать сообщение и делать скриншоты",
    hidden: true,
  },
  reply_to_message_id: {
    required: false,
    validation: INTEGER_REGEXP,
    hidden: true,
  },
  allow_sending_without_reply: {
    required: false,
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    hidden: true,
  },
  reply_markup: {
    required: false,
    validation: undefined,
    hidden: false,
  },
}
export const SendPhotoParameters: TgMailing.BodyFieldsDefinitions<
  "sendPhoto",
  keyof Omit<Parameters<ApiMethods<any>["sendPhoto"]>[0], "chat_id">
>["sendPhoto"] = {
  photo: {
    required: true,
    validation: URL_REGEXP,
    columnHeaderMatch: PHOTO_COLUMN_HEADER_REGEXP,
    name: "Картинка",
    helperText: "Ссылка на картинку",
  },
  caption: {
    required: false,
    validation: ANY_TEXT_REGEXP,
    columnHeaderMatch: TEXT_COLUMN_HEADER_REGEXP,
    name: "Текст рассылки",
  },
  parse_mode: {
    required: false,
    validation: PARSE_MODE_REGEXP,
    columnHeaderMatch: PARSE_MODE_COLUMN_HEADER_REGEXP,
    name: "Форматирование",
    helperText: ParseModeHelperText,
  },
  protect_content: {
    name: "Защитить контент",
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    helperText: "Запретить пересылать сообщение и делать скриншоты",
    required: false,
    hidden: true,
  },
  disable_notification: {
    name: "Отключить уведомление",
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    columnHeaderMatch: NOTIFICATION_COLUMN_HEADER_REGEXP,
    required: false,
    hidden: false,
  },
  reply_to_message_id: {
    validation: INTEGER_REGEXP,
    required: false,
    hidden: true,
  },
  allow_sending_without_reply: {
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    required: false,
    hidden: true,
  },
  reply_markup: {
    validation: undefined,
    required: false,
    hidden: false,
  },
  caption_entities: {
    validation: undefined,
    required: false,
    hidden: true,
  },
  has_spoiler: {
    name: "Спрятать под спойлер",
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    columnHeaderMatch: SPOILER_COLUMN_HEADER_REGEXP,
    helperText: "Картинка будет размыта, пока на неё не нажмут",
    required: false,
    hidden: false,
  },
  message_thread_id: {
    required: false,
    validation: undefined,
    hidden: true,
  },
}
export const SendAnimationParameters: TgMailing.BodyFieldsDefinitions<
  "sendAnimation",
  keyof Omit<Parameters<ApiMethods<any>["sendAnimation"]>[0], "chat_id">
>["sendAnimation"] = {
  animation: {
    required: true,
    validation: URL_REGEXP,
    columnHeaderMatch: GIF_COLUMN_HEADER_REGEXP,
    name: "Анимация (GIF)",
    helperText: "Ссылка на анимацию",
  },
  caption: {
    required: false,
    validation: ANY_TEXT_REGEXP,
    columnHeaderMatch: TEXT_COLUMN_HEADER_REGEXP,
    name: "Текст рассылки",
  },
  parse_mode: {
    required: false,
    validation: PARSE_MODE_REGEXP,
    columnHeaderMatch: PARSE_MODE_COLUMN_HEADER_REGEXP,
    name: "Форматирование",
    helperText: ParseModeHelperText,
  },
  protect_content: {
    name: "Защитить контент",
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    helperText: "Запретить пересылать сообщение и делать скриншоты",
    required: false,
    hidden: true,
  },
  disable_notification: {
    name: "Отключить уведомление",
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    columnHeaderMatch: NOTIFICATION_COLUMN_HEADER_REGEXP,
    required: false,
    hidden: false,
  },
  reply_to_message_id: {
    validation: INTEGER_REGEXP,
    required: false,
    hidden: true,
  },
  allow_sending_without_reply: {
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    required: false,
    hidden: true,
  },
  reply_markup: {
    validation: undefined,
    required: false,
    hidden: false,
  },
  caption_entities: {
    validation: undefined,
    required: false,
    hidden: true,
  },
  has_spoiler: {
    name: "Спрятать под спойлер",
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    columnHeaderMatch: SPOILER_COLUMN_HEADER_REGEXP,
    helperText: "Обложка анимации будет размыта, пока на неё не нажмут",
    required: false,
    hidden: false,
  },
  message_thread_id: {
    required: false,
    validation: undefined,
    hidden: true,
  },
  duration: {
    required: false,
    validation: undefined,
    hidden: true,
  },
  width: {
    required: false,
    validation: undefined,
    hidden: true,
  },
  height: {
    required: false,
    validation: undefined,
    hidden: true,
  },
  thumbnail: {
    required: false,
    validation: undefined,
    hidden: true,
  },
}
export const SendVideoParameters: TgMailing.BodyFieldsDefinitions<
  "sendVideo",
  keyof Omit<Parameters<ApiMethods<any>["sendVideo"]>[0], "chat_id">
>["sendVideo"] = {
  video: {
    required: true,
    validation: URL_REGEXP,
    columnHeaderMatch: VIDEO_COLUMN_HEADER_REGEXP,
    name: "Видео",
    helperText: "Ссылка на видео",
  },
  caption: {
    required: false,
    validation: ANY_TEXT_REGEXP,
    columnHeaderMatch: TEXT_COLUMN_HEADER_REGEXP,
    name: "Текст рассылки",
  },
  parse_mode: {
    required: false,
    validation: PARSE_MODE_REGEXP,
    columnHeaderMatch: PARSE_MODE_COLUMN_HEADER_REGEXP,
    name: "Форматирование",
    helperText: ParseModeHelperText,
  },
  protect_content: {
    name: "Защитить контент",
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    helperText: "Запретить пересылать сообщение и делать скриншоты",
    required: false,
    hidden: true,
  },
  disable_notification: {
    name: "Отключить уведомление",
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    columnHeaderMatch: NOTIFICATION_COLUMN_HEADER_REGEXP,
    required: false,
    hidden: false,
  },
  reply_to_message_id: {
    validation: INTEGER_REGEXP,
    required: false,
    hidden: true,
  },
  allow_sending_without_reply: {
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    required: false,
    hidden: true,
  },
  reply_markup: {
    validation: undefined,
    required: false,
    hidden: false,
  },
  caption_entities: {
    validation: undefined,
    required: false,
    hidden: true,
  },
  has_spoiler: {
    name: "Спрятать под спойлер",
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    columnHeaderMatch: SPOILER_COLUMN_HEADER_REGEXP,
    helperText: "Обложка видео будет размыта, пока на него не нажмут",
    required: false,
    hidden: false,
  },
  message_thread_id: {
    required: false,
    validation: undefined,
    hidden: true,
  },
  duration: {
    required: false,
    validation: undefined,
    hidden: true,
  },
  width: {
    required: false,
    validation: undefined,
    hidden: true,
  },
  height: {
    required: false,
    validation: undefined,
    hidden: true,
  },
  thumbnail: {
    required: false,
    validation: undefined,
    hidden: true,
  },
  supports_streaming: {
    required: false,
    validation: undefined,
    parseValue: BOOLEAN_PARSER,
    hidden: true,
    default: true,
  },
}
export const SendChatActionParameters: TgMailing.BodyFieldsDefinitions<
  "sendChatAction",
  keyof Omit<Parameters<ApiMethods<any>["sendChatAction"]>[0], "chat_id">
>["sendChatAction"] = {
  action: {
    required: true,
    validation: CHAT_ACTION_REGEXP,
    hidden: true,
    default: "typing",
  },
  message_thread_id: {
    required: false,
    validation: undefined,
    hidden: true,
  },
}

export const ForwardMessagesParameters: TgMailing.BodyFieldsDefinitions<
  "forwardMessages",
  keyof Omit<TgMailing.MtprotoForwardMessagesBody, "chat_id">
>["forwardMessages"] = {
  from_chat_id: {
    required: true,
    validation: CHAT_ID_LIKE_REGEXP,
    parseValue: parseChatIdLike,
    columnHeaderMatch: FROM_CHAT_ID_COLUMN_HEADER_REGEXP,
    name: "Источник (chat_id или @username)",
    helperText:
      "Числовой ID канала/чата (например, -100…) или @username публичного канала",
  },
  message_ids: {
    required: true,
    validation: MESSAGE_IDS_REGEXP,
    parseValue: parseMessageIds,
    columnHeaderMatch: MESSAGE_IDS_COLUMN_HEADER_REGEXP,
    name: "ID сообщений",
    helperText: "Числа через запятую или пробел: 12, 13, 14",
  },
  disable_notification: {
    name: "Отключить уведомление",
    required: false,
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    columnHeaderMatch: NOTIFICATION_COLUMN_HEADER_REGEXP,
    hidden: false,
  },
  protect_content: {
    name: "Защитить контент",
    required: false,
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    helperText: "Запретить пересылать сообщение и делать скриншоты",
    columnHeaderMatch: PROTECT_CONTENT_COLUMN_HEADER_REGEXP,
    hidden: false,
  },
  drop_author: {
    name: "Скрыть автора",
    required: false,
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    helperText:
      "Переслать без подписи «Forwarded from …», просмотры не будут учитываться",
    columnHeaderMatch: DROP_AUTHOR_COLUMN_HEADER_REGEXP,
    hidden: false,
  },
}

export const CopyMessagesParameters: TgMailing.BodyFieldsDefinitions<
  "copyMessages",
  keyof Omit<TgMailing.MtprotoCopyMessagesBody, "chat_id">
>["copyMessages"] = {
  from_chat_id: {
    required: true,
    validation: CHAT_ID_LIKE_REGEXP,
    parseValue: parseChatIdLike,
    columnHeaderMatch: FROM_CHAT_ID_COLUMN_HEADER_REGEXP,
    name: "Источник (chat_id или @username)",
    helperText:
      "Числовой ID канала/чата (например, -100…) или @username публичного канала",
  },
  message_ids: {
    required: true,
    validation: MESSAGE_IDS_REGEXP,
    parseValue: parseMessageIds,
    columnHeaderMatch: MESSAGE_IDS_COLUMN_HEADER_REGEXP,
    name: "ID сообщений",
    helperText: "Числа через запятую или пробел: 12, 13, 14",
  },
  disable_notification: {
    name: "Отключить уведомление",
    required: false,
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    columnHeaderMatch: NOTIFICATION_COLUMN_HEADER_REGEXP,
    hidden: false,
  },
  protect_content: {
    name: "Защитить контент",
    required: false,
    validation: BOOL_REGEXP,
    parseValue: BOOLEAN_PARSER,
    helperText: "Запретить пересылать сообщение и делать скриншоты",
    columnHeaderMatch: PROTECT_CONTENT_COLUMN_HEADER_REGEXP,
    hidden: false,
  },
}

// ─── length / url / message-ids helpers ─────────────────────────────────────

export const TEXT_MAX_LENGTH = 4096
export const CAPTION_MAX_LENGTH = 1024

/** Factory: returns a validator that checks `value.length <= max`. */
export const validateTextLength =
  (max: number) =>
  (value: unknown): true | string => {
    const len = typeof value === "string" ? value.length : 0
    return len <= max ? true : `Превышен лимит: ${len} / ${max} символов`
  }

/** Validates that the value is an http(s) URL (or empty — required is separate). */
export const validateMediaUrl = (value: unknown): true | string => {
  if (!value || (typeof value === "string" && !value.trim())) return true
  return URL_REGEXP.test(String(value))
    ? true
    : "Ожидается ссылка вида https://…"
}

/** Validates that value is a list of integers separated by commas / spaces. */
export const validateMessageIdsField = (value: unknown): true | string => {
  if (!value || (typeof value === "string" && !value.trim())) return true
  return MESSAGE_IDS_REGEXP.test(String(value).trim())
    ? true
    : "Введите числа через запятую или пробел: 12, 13, 14"
}

// ─── HTML markup validator ───────────────────────────────────────────────────

const HTML_ALLOWED_TAGS = new Set([
  "b",
  "strong",
  "i",
  "em",
  "u",
  "ins",
  "s",
  "strike",
  "del",
  "span",
  "a",
  "code",
  "pre",
  "blockquote",
  "tg-spoiler",
  "tg-emoji",
  "tg-time",
])
const HTML_NAMED_ENTITIES = new Set(["lt", "gt", "amp", "quot"])

export const validateHtmlMarkup = (value: unknown): true | string => {
  if (typeof value !== "string" || !value.trim()) return true

  const text = value
  const stack: string[] = [] // open-tag stack for nesting/balance checks
  let i = 0

  while (i < text.length) {
    const ch = text[i]

    // ── Bare & ───────────────────────────────────────────────────────────────
    if (ch === "&") {
      const semi = text.indexOf(";", i + 1)
      if (semi === -1)
        return `Символ «&» в позиции ${i + 1} не экранирован — используйте &amp;`
      const entity = text.slice(i + 1, semi)
      if (
        !entity.startsWith("#") &&
        !HTML_NAMED_ENTITIES.has(entity.toLowerCase())
      )
        return `Неподдерживаемая HTML-сущность &${entity}; — разрешены &lt; &gt; &amp; &quot; и числовые (&#N;)`
      i = semi + 1
      continue
    }

    // ── Tag ──────────────────────────────────────────────────────────────────
    if (ch === "<") {
      const gt = text.indexOf(">", i + 1)
      if (gt === -1)
        return `Незакрытый тег начиная с позиции ${i + 1} — отсутствует «>»`

      const inner = text.slice(i + 1, gt)
      const isClosing = inner.trimStart().startsWith("/")
      const rawBody = inner.replace(/^\//, "").trimStart()
      const nameMatch = rawBody.match(/^([a-zA-Z][a-zA-Z0-9-]*)/)
      if (!nameMatch) return `Неверный тег в позиции ${i + 1}`

      const tagName = nameMatch[1].toLowerCase()

      if (!HTML_ALLOWED_TAGS.has(tagName))
        return `Тег <${tagName}> не поддерживается Telegram. Разрешены: ${[...HTML_ALLOWED_TAGS].join(", ")}`

      if (!isClosing) {
        // Attribute validation
        if (tagName === "span" && !inner.includes("tg-spoiler"))
          return `<span> разрешён только с атрибутом class="tg-spoiler"`
        if (tagName === "a" && !inner.includes("href"))
          return `Тег <a> должен содержать атрибут href`

        // Nesting rules
        const parent = stack[stack.length - 1]
        if (parent === "pre" && tagName !== "code")
          return `Внутри <pre> разрешён только <code class="language-..."> — нельзя вкладывать <${tagName}>`
        if (tagName === "blockquote" && stack.includes("blockquote"))
          return `<blockquote> нельзя вкладывать в другой <blockquote>`

        stack.push(tagName)
      } else {
        // Closing tag
        if (stack.length === 0)
          return `Закрывающий тег </${tagName}> в позиции ${i + 1} не имеет пары`
        const top = stack[stack.length - 1]
        if (top !== tagName)
          return `Закрывающий тег </${tagName}> не соответствует открытому <${top}>`
        stack.pop()
      }

      i = gt + 1
      continue
    }

    // ── Bare > ───────────────────────────────────────────────────────────────
    if (ch === ">")
      return `Символ «>» в позиции ${i + 1} не экранирован — используйте &gt;`

    i++
  }

  if (stack.length > 0) return `Незакрытый тег: <${stack[stack.length - 1]}>`

  return true
}

// ─── MarkdownV2 markup validator ─────────────────────────────────────────────

const MDV2_SPECIAL = new Set("_*[]()~`>#+-=|{}.!\\".split(""))

/**
 * Recursively validates a MarkdownV2 segment.
 * @param text  full input string
 * @param start start index in text
 * @param closer marker that terminates this segment (null = top-level)
 * @returns [endIndex, errorMessage|null]
 *   endIndex points to the char AFTER the closer (or text.length at top-level).
 */
function validateMdV2Segment(
  text: string,
  start: number,
  closer: string | null,
): [number, string | null] {
  let i = start

  while (i < text.length) {
    // ── Check for closer ────────────────────────────────────────────────────
    if (closer !== null) {
      if ((closer === "||" || closer === "__") && text.startsWith(closer, i))
        return [i + closer.length, null]
      if (closer.length === 1 && text[i] === closer) return [i + 1, null]
    }

    const ch = text[i]

    // ── 1. Escape sequence (\X) ─────────────────────────────────────────────
    if (ch === "\\") {
      if (i + 1 >= text.length)
        return [
          i,
          "Незавершённая escape-последовательность (\\) в конце текста",
        ]
      i += 2
      continue
    }

    // ── 2. Code block ``` ───────────────────────────────────────────────────
    if (text.startsWith("```", i)) {
      const end = text.indexOf("```", i + 3)
      if (end === -1)
        return [i, "Незакрытый блок кода (```) — добавьте закрывающие ```"]
      i = end + 3
      continue
    }

    // ── 3. Inline code ` ────────────────────────────────────────────────────
    if (ch === "`") {
      const end = text.indexOf("`", i + 1)
      if (end === -1)
        return [i, "Незакрытый инлайн-код (`) — добавьте закрывающую `"]
      i = end + 1
      continue
    }

    // ── 4. Custom emoji / datetime: ![alt](tg://...) ────────────────────────
    if (ch === "!" && text[i + 1] === "[") {
      const bracketEnd = text.indexOf("]", i + 2)
      if (bracketEnd !== -1 && text[bracketEnd + 1] === "(") {
        const urlEnd = text.indexOf(")", bracketEnd + 2)
        if (urlEnd === -1)
          return [i, "Незакрытый custom emoji/datetime — отсутствует «)»"]
        i = urlEnd + 1
        continue
      }
      return [
        i,
        `Символ «!» в позиции ${i + 1} должен быть экранирован (\\!) ` +
          "или оформлен как ![alt](tg://...)",
      ]
    }

    // ── 5. Inline link [text](url) ─────────────────────────────────────────
    if (ch === "[") {
      let j = i + 1,
        depth = 1
      while (j < text.length && depth > 0) {
        if (text[j] === "\\") {
          j += 2
          continue
        }
        if (text[j] === "[") depth++
        else if (text[j] === "]") depth--
        j++
      }
      if (depth !== 0)
        return [
          i,
          `Символ «[» в позиции ${i + 1} должен быть экранирован (\\[) или закрыт`,
        ]
      // j now = bracketEnd + 1
      if (text[j] === "(") {
        // Skip URL — inside (...) only ) and \ need escaping; balance nested ()
        let urlDepth = 1
        let k = j + 1
        while (k < text.length && urlDepth > 0) {
          if (text[k] === "\\") {
            k += 2
            continue
          }
          if (text[k] === "(") urlDepth++
          else if (text[k] === ")") urlDepth--
          k++
        }
        if (urlDepth !== 0)
          return [i, "Незакрытая ссылка [text](url) — отсутствует «)»"]
        i = k
        continue
      }
      return [
        i,
        `Символ «[» в позиции ${i + 1} должен быть экранирован (\\[) или оформлен как [text](url)`,
      ]
    }

    // ── 6. Expandable blockquote **>lines...|| ────────────────────────────
    //    Only at position 0 or after newline
    if ((i === 0 || text[i - 1] === "\n") && text.startsWith("**>", i)) {
      // consume lines starting with > until trailing || or no more > lines
      i += 3 // skip **>
      while (i < text.length) {
        const nlIdx = text.indexOf("\n", i)
        const lineEnd = nlIdx === -1 ? text.length : nlIdx
        const line = text.slice(i, lineEnd)
        if (line.endsWith("||")) {
          i = lineEnd === text.length ? text.length : lineEnd + 1
          break
        }
        i = lineEnd === text.length ? text.length : lineEnd + 1
        if (i < text.length && text[i] !== ">") break
        i++ // skip the leading >
      }
      continue
    }

    // ── 7. Blockquote > at line start ─────────────────────────────────────
    if ((i === 0 || text[i - 1] === "\n") && ch === ">") {
      i++
      continue
    }

    // ── 8. Spoiler ||...|| ─────────────────────────────────────────────────
    if (text.startsWith("||", i)) {
      const [end, err] = validateMdV2Segment(text, i + 2, "||")
      if (err) return [i, err]
      i = end
      continue
    }

    // ── 9. Underline __...__ ───────────────────────────────────────────────
    if (text.startsWith("__", i)) {
      const [end, err] = validateMdV2Segment(text, i + 2, "__")
      if (err) return [i, err]
      i = end
      continue
    }

    // ── 10. Bold *...* ─────────────────────────────────────────────────────
    if (ch === "*") {
      const [end, err] = validateMdV2Segment(text, i + 1, "*")
      if (err) return [i, err]
      if (end === i + 1)
        return [i, "Незакрытое жирное выделение (*) — добавьте закрывающий *"]
      i = end
      continue
    }

    // ── 11. Italic _..._ ───────────────────────────────────────────────────
    if (ch === "_") {
      const [end, err] = validateMdV2Segment(text, i + 1, "_")
      if (err) return [i, err]
      if (end === i + 1)
        return [i, "Незакрытый курсив (_) — добавьте закрывающий _"]
      i = end
      continue
    }

    // ── 12. Strikethrough ~...~ ────────────────────────────────────────────
    if (ch === "~") {
      const [end, err] = validateMdV2Segment(text, i + 1, "~")
      if (err) return [i, err]
      if (end === i + 1)
        return [i, "Незакрытое зачёркивание (~) — добавьте закрывающий ~"]
      i = end
      continue
    }

    // ── 13. Unescaped special char ─────────────────────────────────────────
    if (MDV2_SPECIAL.has(ch))
      return [
        i,
        `Символ «${ch}» в позиции ${i + 1} должен быть экранирован: \\${ch}`,
      ]

    i++
  }

  // Reached end-of-string while expecting a closer
  if (closer !== null) {
    const names: Record<string, string> = {
      "*": "жирного выделения (*)",
      _: "курсива (_)",
      __: "подчёркивания (__)",
      "~": "зачёркивания (~)",
      "||": "спойлера (||)",
    }
    return [i, `Незакрытый маркер ${names[closer] ?? closer}`]
  }

  return [i, null]
}

export const validateMarkdownV2Markup = (value: unknown): true | string => {
  if (typeof value !== "string" || !value.trim()) return true
  const [, error] = validateMdV2Segment(value, 0, null)
  return error ?? true
}

// ─── Legacy Markdown validator ───────────────────────────────────────────────

export const validateMarkdownMarkup = (value: unknown): true | string => {
  if (typeof value !== "string" || !value.trim()) return true

  const text = value
  let i = 0
  /** Currently open entity marker, or null if top-level. */
  let openMarker: "*" | "_" | "`" | null = null

  while (i < text.length) {
    const ch = text[i]

    // ── Escape sequences ────────────────────────────────────────────────────
    if (ch === "\\") {
      if (openMarker !== null) {
        // Escaping inside an entity is not allowed in legacy Markdown
        const next = text[i + 1]
        if (next === "_" || next === "*" || next === "`" || next === "[")
          return `Экранирование внутри ${openMarker}…${openMarker} не допускается в legacy Markdown — закройте и откройте заново`
      } else {
        i += 2 // consume \X
        continue
      }
    }

    // ── Code block ``` ───────────────────────────────────────────────────────
    if (text.startsWith("```", i)) {
      if (openMarker !== null)
        return `Незакрытый ${openMarker}…${openMarker} перед блоком кода`
      const end = text.indexOf("```", i + 3)
      if (end === -1)
        return "Незакрытый блок кода (```) — добавьте закрывающие ```"
      i = end + 3
      continue
    }

    // ── Entity markers * _ ` ────────────────────────────────────────────────
    if (ch === "*" || ch === "_" || ch === "`") {
      const marker = ch as "*" | "_" | "`"
      if (openMarker === null) {
        openMarker = marker
        i++
        continue
      }
      if (openMarker === marker) {
        openMarker = null // close
        i++
        continue
      }
      // Different marker inside open entity → nesting not allowed
      return `Нельзя открыть ${marker}…${marker} внутри ${openMarker}…${openMarker}: вложенность не поддерживается в legacy Markdown`
    }

    // ── Inline link [text](url) ─────────────────────────────────────────────
    if (ch === "[") {
      if (openMarker !== null)
        return `Ссылка [text](url) внутри ${openMarker}…${openMarker} не поддерживается в legacy Markdown`
      const bracketEnd = text.indexOf("]", i + 1)
      if (bracketEnd === -1)
        return `Незакрытая скобка «[» в позиции ${i + 1} — ожидается [text](url)`
      if (text[bracketEnd + 1] !== "(")
        return `После «]» ожидается «(url)» в позиции ${bracketEnd + 1}`
      const urlEnd = text.indexOf(")", bracketEnd + 2)
      if (urlEnd === -1) return "Незакрытая ссылка — отсутствует «)»"
      i = urlEnd + 1
      continue
    }

    i++
  }

  if (openMarker !== null)
    return `Незакрытый маркер ${openMarker}…${openMarker}`

  return true
}

export const BodyFieldsPropperties = {
  sendMessage: { ...BaseParameters, ...SendMessageParameters },
  sendChatAction: { ...BaseParameters, ...SendChatActionParameters },
  sendPhoto: { ...BaseParameters, ...SendPhotoParameters },
  sendVideo: { ...BaseParameters, ...SendVideoParameters },
  sendAnimation: { ...BaseParameters, ...SendAnimationParameters },
  forwardMessages: { ...BaseParameters, ...ForwardMessagesParameters },
  copyMessages: { ...BaseParameters, ...CopyMessagesParameters },
} as const
